import { Field, InputType, Int, ObjectType, registerEnumType } from 'type-graphql'

import { ScalarObject } from '@things-factory/shell'

export enum DataItemType {
  number = 'number',
  text = 'text',
  boolean = 'boolean',
  select = 'select',
  radio = 'radio',
  date = 'date',
  datetime = 'datetime',
  file = 'file',
  image = 'image',
  video = 'video',
  audio = 'audio',
  signature = 'signature'
}

registerEnumType(DataItemType, {
  name: 'DataItemType',
  description: 'type enumeration of a data-item'
})

export enum DataItemStatType {
  sum = 'sum',
  mean = 'mean',
  stddev = 'stddev',
  variance = 'variance',
  min = 'min',
  max = 'max',
  range = 'range',
  median = 'median',
  mode = 'mode'
}

registerEnumType(DataItemStatType, {
  name: 'DataItemStatType',
  description: 'stat/agg type enumeration of a data-item'
})

@ObjectType({ description: 'Entity for DataItem' })
export class DataItem {
  @Field({ description: 'The name of the data item' })
  name: string

  @Field({ nullable: true, description: 'A description of the data item' })
  description?: string

  @Field({
    nullable: true,
    description:
      'Specifies a key name to be used as a property in a JSON-like object, representing a subfield of a dataset record.'
  })
  tag?: string

  @Field({
    nullable: true,
    description:
      'Specifies a grouping identifier for data items with related content, allowing them to be displayed as subgroups within the overall dataset.'
  })
  group?: string

  @Field({
    nullable: true,
    description:
      'Specifies a secondary grouping identifier for data items with related content, allowing them to be displayed as subgroups within the overall dataset.'
  })
  subgroup?: string

  @Field({ nullable: true, description: 'Indicates if the data item is active' })
  active?: boolean

  @Field({ nullable: true, description: 'Indicates if the data item is hidden' })
  hidden?: boolean

  @Field({ nullable: true, description: 'The type of the data item' })
  type?: DataItemType

  @Field(type => ScalarObject, { nullable: true, description: 'Options associated with the data item type' })
  options?: { [option: string]: any }

  @Field({
    nullable: true,
    description: 'The grouping logic for data finalize in the given field during periodic task deadlines.'
  })
  stat?: DataItemStatType

  @Field({
    nullable: true,
    description: 'Aggregation functions to be used in data summarizing logic for a given period.'
  })
  agg?: DataItemStatType

  @Field({ nullable: true, description: 'The unit of measurement for the data item' })
  unit?: string

  @Field(type => Int, { nullable: true, description: 'The maximum number of data values allowed for this field' })
  quota?: number

  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Specifies the valid ranges and parameters for this data item.'
  })
  spec?: { [key: string]: any }
}

@InputType()
export class DataItemPatch {
  @Field({ nullable: true, description: 'The name of the data item' })
  name?: string

  @Field({ nullable: true, description: 'A description of the data item' })
  description?: string

  @Field({
    nullable: true,
    description:
      'Specifies a key name to be used as a property in a JSON-like object, representing a subfield of a dataset record.'
  })
  tag?: string

  @Field({
    nullable: true,
    description:
      'Specifies a grouping identifier for data items with related content, allowing them to be displayed as subgroups within the overall dataset.'
  })
  group?: string

  @Field({
    nullable: true,
    description:
      'Specifies a secondary grouping identifier for data items with related content, allowing them to be displayed as subgroups within the overall dataset.'
  })
  subgroup?: string

  @Field(type => DataItemType, { nullable: true, description: 'The type of the data item' })
  type?: DataItemType

  @Field(type => ScalarObject, { nullable: true, description: 'Options associated with the data item type' })
  options?: { [option: string]: any }

  @Field({
    nullable: true,
    description: 'The grouping logic for data summarization in the given field during periodic task deadlines.'
  })
  stat?: DataItemStatType

  @Field({
    nullable: true,
    description: 'Aggregation functions to be used in data summarizing logic for a given period.'
  })
  agg?: DataItemStatType

  @Field({ nullable: true, description: 'The unit of measurement for the data item' })
  unit?: string

  @Field(type => Int, { nullable: true, description: 'The maximum number of data values allowed for this field' })
  quota?: number

  @Field({ nullable: true, description: 'Indicates if the data item is active' })
  active?: boolean

  @Field({ nullable: true, description: 'Indicates if the data item is hidden' })
  hidden?: boolean

  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Specifies the valid ranges and parameters for this data item.'
  })
  spec?: { [key: string]: any }
}

/**
 * ============================================================================
 * Media Type Options Schema
 * ============================================================================
 *
 * The `options` field provides type-specific configuration for media types
 * (image, video, audio). Below are the recommended schemas for each type.
 *
 * ============================================================================
 * IMAGE Type Options
 * ============================================================================
 *
 * Example:
 * {
 *   type: 'image',
 *   options: {
 *     // File formats
 *     accept: ['jpeg', 'png', 'webp', 'gif'],  // Allowed formats
 *
 *     // Size constraints
 *     maxSize: 10485760,      // Max file size in bytes (10MB)
 *     maxWidth: 4096,         // Max width in pixels
 *     maxHeight: 4096,        // Max height in pixels
 *     minWidth: 224,          // Min width in pixels
 *     minHeight: 224,         // Min height in pixels
 *
 *     // Multiple uploads
 *     multiple: true,         // Allow multiple images
 *     maxFiles: 10,           // Max number of files
 *
 *     // Thumbnail generation
 *     thumbnail: {
 *       enabled: true,
 *       width: 200,
 *       height: 200,
 *       format: 'jpeg',       // 'jpeg' | 'png' | 'webp'
 *       quality: 85           // 1-100
 *     },
 *
 *     // Preview settings
 *     preview: {
 *       enabled: true,
 *       maxWidth: 1024,
 *       maxHeight: 1024
 *     },
 *
 *     // Metadata extraction
 *     metadata: {
 *       extractExif: true,           // Extract EXIF data
 *       extractDimensions: true,     // Extract width/height
 *       extractFormat: true,         // Extract image format
 *       extractColorProfile: true    // Extract color profile
 *     },
 *
 *     // Compression/conversion
 *     compression: {
 *       enabled: true,
 *       format: 'webp',       // Target format
 *       quality: 85,          // Compression quality
 *       maxDimension: 2048    // Max width or height
 *     }
 *   }
 * }
 *
 * ============================================================================
 * VIDEO Type Options
 * ============================================================================
 *
 * Example:
 * {
 *   type: 'video',
 *   options: {
 *     // File formats
 *     accept: ['mp4', 'webm', 'mov', 'avi'],  // Allowed formats
 *
 *     // Size constraints
 *     maxSize: 104857600,     // Max file size in bytes (100MB)
 *     maxDuration: 300,       // Max duration in seconds (5 minutes)
 *     maxWidth: 1920,         // Max resolution width
 *     maxHeight: 1080,        // Max resolution height
 *
 *     // Multiple uploads
 *     multiple: false,        // Usually single video per field
 *     maxFiles: 1,
 *
 *     // Thumbnail generation
 *     thumbnail: {
 *       enabled: true,
 *       width: 320,
 *       height: 180,
 *       format: 'jpeg',
 *       quality: 85,
 *       captureTime: 1        // Capture thumbnail at N seconds
 *     },
 *
 *     // Preview settings
 *     preview: {
 *       enabled: true,
 *       maxWidth: 640,
 *       maxHeight: 480,
 *       controls: true,       // Show video controls
 *       autoplay: false,      // Autoplay preview
 *       muted: true           // Mute by default
 *     },
 *
 *     // Metadata extraction
 *     metadata: {
 *       extractDuration: true,       // Extract video duration
 *       extractResolution: true,     // Extract width/height
 *       extractFormat: true,         // Extract video format
 *       extractCodec: true,          // Extract video/audio codec
 *       extractFrameRate: true,      // Extract FPS
 *       extractBitrate: true         // Extract bitrate
 *     },
 *
 *     // Transcoding
 *     transcoding: {
 *       enabled: true,
 *       format: 'mp4',        // Target format
 *       codec: 'h264',        // Video codec
 *       quality: 'medium',    // 'low' | 'medium' | 'high'
 *       maxDimension: 1280    // Scale down if larger
 *     }
 *   }
 * }
 *
 * ============================================================================
 * AUDIO Type Options
 * ============================================================================
 *
 * Example:
 * {
 *   type: 'audio',
 *   options: {
 *     // File formats
 *     accept: ['mp3', 'wav', 'ogg', 'm4a', 'aac'],  // Allowed formats
 *
 *     // Size constraints
 *     maxSize: 52428800,      // Max file size in bytes (50MB)
 *     maxDuration: 600,       // Max duration in seconds (10 minutes)
 *
 *     // Multiple uploads
 *     multiple: false,
 *     maxFiles: 1,
 *
 *     // Waveform visualization
 *     waveform: {
 *       enabled: true,
 *       width: 800,
 *       height: 100,
 *       color: '#3498db'
 *     },
 *
 *     // Preview settings
 *     preview: {
 *       enabled: true,
 *       controls: true,       // Show audio controls
 *       autoplay: false,      // Autoplay preview
 *       visualizer: true      // Show audio visualizer
 *     },
 *
 *     // Metadata extraction
 *     metadata: {
 *       extractDuration: true,       // Extract audio duration
 *       extractFormat: true,         // Extract audio format
 *       extractCodec: true,          // Extract audio codec
 *       extractBitrate: true,        // Extract bitrate
 *       extractSampleRate: true,     // Extract sample rate
 *       extractChannels: true,       // Extract channel count
 *       extractID3Tags: true         // Extract ID3 tags (artist, title, etc.)
 *     },
 *
 *     // Transcoding
 *     transcoding: {
 *       enabled: true,
 *       format: 'mp3',        // Target format
 *       codec: 'mp3',         // Audio codec
 *       bitrate: 128,         // kbps
 *       sampleRate: 44100,    // Hz
 *       channels: 2           // Stereo
 *     }
 *   }
 * }
 *
 * ============================================================================
 * Usage in GraphQL
 * ============================================================================
 *
 * mutation {
 *   updateDataSet(
 *     name: "product_inspection"
 *     patch: {
 *       dataItems: [
 *         {
 *           name: "product_image"
 *           type: image
 *           options: {
 *             accept: ["jpeg", "png"]
 *             maxSize: 10485760
 *             thumbnail: { enabled: true, width: 200, height: 200 }
 *             metadata: { extractExif: true, extractDimensions: true }
 *           }
 *         },
 *         {
 *           name: "inspection_video"
 *           type: video
 *           options: {
 *             accept: ["mp4", "webm"]
 *             maxSize: 104857600
 *             maxDuration: 120
 *             thumbnail: { enabled: true, captureTime: 1 }
 *           }
 *         },
 *         {
 *           name: "operator_note_audio"
 *           type: audio
 *           options: {
 *             accept: ["mp3", "wav"]
 *             maxSize: 10485760
 *             maxDuration: 60
 *             waveform: { enabled: true }
 *           }
 *         }
 *       ]
 *     }
 *   ) {
 *     id
 *     name
 *   }
 * }
 *
 * ============================================================================
 * Label Studio Integration
 * ============================================================================
 *
 * Dataset media types map to Label Studio annotation types:
 *
 * - image → <Image name="image" value="$image"/>
 * - video → <Video name="video" value="$video"/>
 * - audio → <Audio name="audio" value="$audio"/>
 *
 * Example Label Studio config generation:
 *
 * const dataItem = { name: 'product_image', type: 'image' }
 *
 * // → Label Studio config
 * <View>
 *   <Image name="product_image" value="$product_image"/>
 *   <Choices name="classification" toName="product_image">
 *     <Choice value="Good"/>
 *     <Choice value="Defect"/>
 *   </Choices>
 * </View>
 *
 * ============================================================================
 */
